import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { List, Map, mergeDeep } from 'immutable';
import { ComponentConfigContext } from '../common/ComponentConfigContext';
import { connect, dispatch } from '../../store';
import {
  configureTable,
  filterFormKey,
  isClientSide,
  mountTable,
  unmountTable,
} from './Table.redux';
import { generateKey } from '../../helpers';
import { generateForm } from '../form/Form';
import { FormState } from '../form/FormState';

const fromColumnSet = (columns, columnSet) =>
  columnSet.map(cs => columns.find(c => c.get('value') === cs));

const KeyWrapper = ({ children }) => children;

const TableComponent = props => {
  if (props.configured) {
    const {
      children,
      loading,
      initializing,
      rows,
      error,
      appliedFilters,
      components,
      tableKey,
      filterFormKey,
      count,
      extraData,
    } = props;

    const columnControl = buildColumnControl(props);
    const filterControl = buildFilterControl(props);
    const table = buildTable({ ...props, columnControl });
    const filter = components.FilterForm
      ? buildFilterForm({
          ...props,
          renderers: {
            filterControl: args => buildFilterControl({ args, ...props }),
          },
        })
      : buildFilterLayout(props);
    const pagination = buildPaginationControl(props);

    return children({
      tableKey,
      filterFormKey,
      table,
      filter,
      appliedFilters,
      pagination,
      columnControl,
      filterControl,
      initializing,
      loading,
      rows,
      count,
      error,
      extraData,
    });
  }
  return null;
};

const filterComponentByType = (components, type = 'text') =>
  type === 'boolean' ? components.BooleanFilter : components.TextFilter;

const buildField = ({
  filter,
  filters,
  components,
  columnComponents,
  tableKey,
  tableOptions,
  renderOptions,
}) => {
  const value = filter.get('value');
  const name = filter.getIn(['column', 'value']);
  const title = filter.getIn(['column', 'title']);
  const options = filter.getIn(['column', 'options'], () => [])(
    tableOptions,
    renderOptions,
  );
  const onChange = (value, filterName = name) => {
    dispatch('SET_FILTER', {
      tableKey,
      filter: filterName,
      value,
    });
  };

  const Filter = columnComponents.getIn(
    [name, 'Filter'],
    filterComponentByType(components, filter.getIn(['column', 'type'])),
  );

  return (
    <Filter
      value={value}
      name={name}
      title={title}
      onChange={onChange}
      options={options}
      tableOptions={tableOptions}
      filters={filters}
      visible
    />
  );
};

const filtersToFields = components =>
  Map({
    FormButtons: components.FilterFormButtons,
    FormError: components.FilterFormError,
    FormLayout: components.FilterFormLayout,

    AttributesField: components.AttributesFilter,
    CheckboxField: components.CheckboxFilter,
    CodeField: components.CodeFilter,
    DateField: components.DateFilter,
    DatetimeField: components.DatetimeFilter,
    FormField: components.FormFilter,
    FormMultiField: components.FormMultiFilter,
    PasswordField: components.PasswordFilter,
    RadioField: components.RadioFilter,
    TeamField: components.TeamFilter,
    TeamMultiField: components.TeamMultiFilter,
    SelectField: components.SelectFilter,
    SelectMultiField: components.SelectMultiFilter,
    TextField: components.TextFilter,
    TextMultiField: components.TextMultiFilter,
    TimeField: components.TimeFilter,
    UserField: components.UserFilter,
    UserMultiField: components.UserMultiFilter,
    TableField: components.TableFilter,
  }).filter(c => !!c);

const buildFilterForm = props => {
  const FilterForm = props.components.FilterForm;
  // Build the form filter components.
  const components = filtersToFields(props.components);
  return (
    <FilterForm
      {...props.tableOptions}
      formKey={props.filterFormKey}
      tableKey={props.tableKey}
      components={components}
      renderers={props.renderers}
      alterFields={props.alterFilters}
      fieldSet={props.filterSet}
      onSave={props.onSearch}
      appliedFilters={props.appliedFilters}
      autoFocus={props.filterAutoFocus}
    />
  );
};

const buildFilterLayout = ({
  components,
  columnComponents,
  filters,
  appliedFilters,
  validFilters,
  tableKey,
  columnSet,
  loading,
  initializing,
  tableOptions,
  renderOptions,
}) => {
  // Add an onChange to each filter and convert it to a list for looping.
  const f = filters.map(filter =>
    buildField({
      appliedFilters,
      filter,
      filters,
      components,
      columnComponents,
      tableKey,
      tableOptions,
      renderOptions,
    }),
  );

  const onSearch = e => {
    if (e) {
      e.preventDefault();
    }
    dispatch('APPLY_FILTERS', { tableKey });
  };

  const onReset = e => {
    e.preventDefault();
    dispatch('CLEAR_TABLE_FILTERS', { tableKey });
    dispatch('APPLY_FILTERS', { tableKey });
  };

  const onClear = e => {
    e.preventDefault();
    dispatch('CLEAR_SELECTED_FILTERS', { tableKey });
  };

  const onChangeFilter = (value, filterName) => {
    dispatch('SET_FILTER', {
      tableKey,
      filter: filterName,
      value,
    });
  };

  const FilterLayout = components.FilterLayout;

  return (
    <FilterLayout
      filters={f}
      appliedFilters={appliedFilters}
      validFilters={validFilters}
      onSearch={onSearch}
      onReset={onReset}
      onClear={onClear}
      onChangeFilter={onChangeFilter}
      columnSet={columnSet}
      loading={loading}
      initializing={initializing}
      tableOptions={tableOptions}
      renderOptions={renderOptions}
      tableKey={tableKey}
    />
  );
};

const hasPrevPage = (data, dataSource, tableOptions, pageTokens, pageOffset) =>
  isClientSide(Map({ data, dataSource, tableOptions }))
    ? pageOffset > 0
    : pageTokens.size !== 0;

const hasNextPage = (
  data,
  dataSource,
  tableOptions,
  pageOffset,
  pageSize,
  currentPageToken,
  rows,
) =>
  isClientSide(Map({ data, dataSource, tableOptions }))
    ? rows.size === pageSize
      ? data
        ? pageOffset + pageSize < data.size
        : false
      : !!currentPageToken
    : !!currentPageToken;

const getIndexes = (
  data,
  rows,
  dataSource,
  tableOptions,
  pageOffset,
  pageSize,
  pageTokens,
) =>
  isClientSide(Map({ data, dataSource, tableOptions }))
    ? {
        startIndex: data.size > 0 ? pageOffset + 1 : 0,
        endIndex: data.size > 0 ? pageOffset + rows.size : 0,
      }
    : {
        startIndex: pageTokens.size * pageSize + (rows.size > 0 ? 1 : 0),
        endIndex: pageTokens.size * pageSize + rows.size,
      };

const buildPaginationControl = props => {
  const {
    tableKey,
    filterFormKey,
    data,
    rows,
    dataSource,
    tableOptions,
    pageTokens,
    pageSize,
    pageOffset,
    currentPageToken,
    count,
    components,
    loading,
    renderOptions,
    extraData,
  } = props;
  const PaginationControl = components.PaginationControl;
  const prevPage = hasPrevPage(
    data,
    dataSource,
    tableOptions,
    pageTokens,
    pageOffset,
  )
    ? onPrevPage(tableKey)
    : null;

  const nextPage = hasNextPage(
    data,
    dataSource,
    tableOptions,
    pageOffset,
    pageSize,
    currentPageToken,
    rows,
  )
    ? onNextPage(tableKey)
    : null;

  const { startIndex, endIndex } = getIndexes(
    data,
    rows,
    dataSource,
    tableOptions,
    pageOffset,
    pageSize,
    pageTokens,
  );

  return (
    <PaginationControl
      tableKey={tableKey}
      filterFormKey={filterFormKey}
      prevPage={prevPage}
      nextPage={nextPage}
      gotoPage={onGotoPage(tableKey)}
      loading={loading}
      startIndex={startIndex}
      endIndex={endIndex}
      count={count}
      renderOptions={renderOptions}
      tableOptions={tableOptions}
      extraData={extraData}
    />
  );
};

export const getToggleableColumns = (columns, columnSet, tableKey) =>
  columns.reduce((list, column) => {
    const label =
      (column.get('columnLabel') && column.get('columnLabel').trim()) ||
      (column.get('title') && column.get('title').trim());
    // Include all columns that have a columnLabel or title, and are toggleable
    // or are in the initial columnSet
    if (
      label &&
      (column.get('toggleable') || columnSet.includes(column.get('value')))
    ) {
      return list.push(
        Map({
          value: column.get('value'),
          label,
          checked: columnSet.includes(column.get('value')),
          // Only enable the column toggle if it's toggleable, so that any
          // non-toggleable columns that are in the initial column set will be
          // shown but disabled in the control
          enabled: !!column.get('toggleable'),
          toggle: !!column.get('toggleable')
            ? onToggleColumn(tableKey, column.get('value'))
            : undefined,
        }),
      );
    }
    return list;
  }, List());

const buildColumnControl = props => {
  const {
    tableKey,
    columns,
    columnSet,
    components,
    renderOptions,
    tableOptions,
    extraData,
  } = props;
  const ColumnControl = components.ColumnControl;

  return (
    <ColumnControl
      tableKey={tableKey}
      columns={getToggleableColumns(columns, columnSet, tableKey)}
      renderOptions={renderOptions}
      tableOptions={tableOptions}
      extraData={extraData}
    />
  );
};

const buildFilterControl = ({
  tableKey,
  filterSet,
  appliedFilters,
  components,
  args = {},
}) => {
  const FilterControl = components.FilterControl;

  return (
    <FormState
      formKey={filterFormKey(tableKey)}
      selector={formState => ({
        filterFields: formState?.fields
          ?.map((field, name) =>
            Map({
              name,
              label: field.get('label'),
              value: field.get('value'),
              options: field.get('options'),
              checked: filterSet?.includes(name),
              toggle: onToggleFilter(tableKey, name),
            }),
          )
          ?.toList(),
      })}
    >
      {({ filterFields }) => (
        <FilterControl
          {...args}
          tableKey={tableKey}
          filterFields={filterFields}
          filterCount={appliedFilters?.filter(Boolean)?.size}
        />
      )}
    </FormState>
  );
};

export const buildTable = props => {
  const TableLayout = props.components.TableLayout;
  const header = buildTableHeader(props);
  const body = buildTableBody(props);
  const footer = buildTableFooter(props);

  return (
    <TableLayout
      tableKey={props.tableKey}
      filterFormKey={props.filterFormKey}
      header={header}
      body={body}
      footer={footer}
      initializing={props.initializing}
      loading={props.loading}
      error={props.error}
      empty={props.rows.isEmpty()}
      renderOptions={props.renderOptions}
      tableOptions={props.tableOptions}
      extraData={props.extraData}
    />
  );
};

export const buildTableHeader = props => {
  const Header = props.components.Header;
  const headerRow = buildTableHeaderRow(props);
  return props.omitHeader ? null : (
    <Header
      sortable={props.sortable}
      headerRow={headerRow}
      renderOptions={props.renderOptions}
      rows={props.rows}
      tableOptions={props.tableOptions}
    />
  );
};

export const buildTableHeaderRow = props => {
  const { components, rows, columns, columnSet } = props;
  const HeaderRow = components.HeaderRow;
  const columnHeaders = fromColumnSet(columns, columnSet).map(
    buildTableHeaderCell(props),
  );

  return (
    <HeaderRow
      columnHeaders={columnHeaders}
      renderOptions={props.renderOptions}
      rows={rows}
      tableOptions={props.tableOptions}
    />
  );
};

export const buildTableHeaderCell = props => (column, index) => {
  const {
    tableKey,
    components,
    columnComponents,
    renderOptions,
    tableOptions,
    appliedFilters,
    columnControl,
  } = props;
  const HeaderCell = columnComponents.getIn(
    [column.get('value'), 'HeaderCell'],
    components.HeaderCell,
  );

  const sorting = column === props.sortColumn && props.sortDirection;
  const sortable = props.sortable && column.get('sortable');

  return (
    <KeyWrapper key={`column-${index}`}>
      <HeaderCell
        columnControl={columnControl}
        onSortColumn={onSortColumn(tableKey, column)}
        title={column.get('title')}
        renderOptions={renderOptions}
        sorting={sorting}
        sortable={sortable}
        tableOptions={tableOptions}
        filters={appliedFilters}
      />
    </KeyWrapper>
  );
};

export const buildTableBody = props => {
  const Body = props.components.Body;
  const tableRows = buildTableBodyRows(props);

  return (
    <Body
      loading={props.loading}
      initializing={props.initializing}
      renderOptions={props.renderOptions}
      tableOptions={props.tableOptions}
      tableRows={tableRows}
      error={props.error}
      empty={props.rows.isEmpty()}
    />
  );
};

export const buildTableBodyRows = props => {
  const {
    components,
    rows,
    columns,
    columnSet,
    appliedFilters,
    renderOptions,
    tableOptions,
  } = props;
  const BodyRow = components.BodyRow;
  const EmptyBodyRow = components.EmptyBodyRow;

  return rows.size > 0 ? (
    rows.map((row, index) => {
      const cells = buildTableBodyCells(props, row, index);
      return (
        <KeyWrapper key={`row-${index}`}>
          <BodyRow
            cells={cells}
            columns={columns}
            row={row}
            index={index}
            renderOptions={renderOptions}
            tableOptions={tableOptions}
          />
        </KeyWrapper>
      );
    })
  ) : (
    <EmptyBodyRow
      colSpan={columnSet.size}
      initializing={props.initializing}
      loading={props.loading}
      appliedFilters={appliedFilters}
      renderOptions={renderOptions}
      tableOptions={tableOptions}
      error={props.error}
    />
  );
};

export const buildTableBodyCells = (props, row, rowIndex) => {
  const {
    tableKey,
    components,
    rows,
    columns,
    columnSet,
    columnComponents,
    appliedFilters,
    renderOptions,
    tableOptions,
    extraData,
  } = props;

  return fromColumnSet(columns, columnSet).map((column, index) => {
    const BodyCell = columnComponents.getIn(
      [column.get('value'), 'BodyCell'],
      components.BodyCell,
    );

    const value = row.get(column.get('value'));

    return (
      <KeyWrapper key={`column-${index}`}>
        <BodyCell
          tableKey={tableKey}
          row={row}
          rowIndex={rowIndex}
          index={index}
          value={value}
          rows={rows}
          column={column}
          filters={appliedFilters}
          renderOptions={renderOptions}
          tableOptions={tableOptions}
          extraData={extraData}
        />
      </KeyWrapper>
    );
  });
};

export const buildTableFooter = props => {
  const {
    components,
    columnSet,
    renderOptions,
    rows,
    tableOptions,
    includeFooter,
  } = props;
  const footerRow = buildTableFooterRow(props);
  const Footer = components.Footer;

  return includeFooter ? (
    <Footer
      rows={rows}
      footerRow={footerRow}
      colSpan={columnSet.size}
      renderOptions={renderOptions}
      tableOptions={tableOptions}
    />
  ) : null;
};

export const buildTableFooterRow = props => {
  const { components, renderOptions, tableOptions } = props;
  const cells = buildTableFooterCells(props);
  const FooterRow = components.FooterRow;

  return (
    <FooterRow
      cells={cells}
      renderOptions={renderOptions}
      tableOptions={tableOptions}
    />
  );
};

export const buildTableFooterCells = props => {
  const {
    components,
    columns,
    columnSet,
    columnComponents,
    renderOptions,
    tableOptions,
  } = props;
  return fromColumnSet(columns, columnSet).map((column, index) => {
    const FooterCell = columnComponents.getIn(
      [column.get('value'), 'FooterCell'],
      components.FooterCell,
    );

    return (
      <KeyWrapper key={`column-${index}`}>
        <FooterCell
          column={column}
          renderOptions={renderOptions}
          tableOptions={tableOptions}
        />
      </KeyWrapper>
    );
  });
};

const onNextPage = tableKey => () => dispatch('NEXT_PAGE', { tableKey });
const onPrevPage = tableKey => () => dispatch('PREV_PAGE', { tableKey });
const onGotoPage = tableKey => pageNumber => () =>
  dispatch('GOTO_PAGE', { tableKey, pageNumber });

const onSortColumn = (tableKey, column) => () =>
  dispatch('SORT_COLUMN', { tableKey, column });

const onToggleColumn = (tableKey, column) => () =>
  dispatch('TOGGLE_COLUMN', { tableKey, column });

const onToggleFilter =
  (tableKey, ...filters) =>
  () =>
    dispatch('TOGGLE_FILTER', { tableKey, filters });

const mapStateToProps = () => (state, props) =>
  state.getIn(['tables', props.tableKey], Map()).toObject();

class TableImplComponent extends Component {
  constructor(props) {
    super(props);
  }

  componentDidMount() {
    if (this.props.mounted && !this.props.configured) {
      configureTable(this.props);
    }
  }

  componentDidUpdate() {
    if (this.props.mounted && !this.props.configured) {
      configureTable(this.props);
    }
  }

  render() {
    return <TableComponent {...this.props} />;
  }
}
const TableImpl = connect(mapStateToProps)(TableImplComponent);

export const generateColumns = (columns, addColumns = [], alterColumns = {}) =>
  List(columns)
    .concat(addColumns)
    .map(c => Map({ ...c, ...alterColumns[c.value], value: c.value }));

export const sortColumns = (columns, columnSet) =>
  columns
    // First sort columns by the columnOrder value if provided
    .sortBy(column =>
      column.get('columnOrder') === 'first'
        ? -1
        : column.get('columnOrder') === 'last'
          ? 1
          : 0,
    )
    // Next reduce the columns list into a list of groups, starting a new group
    // each time we get to a column that's in the columnSet. This will result in
    // each group having at most one columnSet column, and that column will be
    // the first one in the group.
    .reduce(
      (list, column) =>
        columnSet.includes(column.get('value'))
          ? list.push(List([column]))
          : list.update(-1, group =>
              group ? group.push(column) : List([column]),
            ),
      List(),
    )
    // Then sort the groups, taking the first column from each group, finding
    // its index in the column set, and using that index as a sort value. Any
    // groups without a columnSet column will be sorted to the end.
    .sortBy(group => {
      const columnSetIndex = columnSet.findIndex(
        c => c === group.getIn([0, 'value']),
      );
      return columnSetIndex >= 0 ? columnSetIndex : columnSet.size + 1;
    })
    // Lastly flatten the groups back to a list of columns
    .flatten(true);

export const extractColumnComponents = columns =>
  columns
    .filter(c => c.has('components'))
    .reduce(
      (result, current) =>
        result.set(current.get('value'), current.get('components')),
      Map(),
    );

export const generateTable =
  ({
    tableOptions = [],
    filterDataSources = () => ({}),
    filters,
    columns,
    dataSource,
    sortable,
    onValidateFilters,
  }) =>
  props => {
    const tableOptionProps = tableOptions
      ? tableOptions.reduce((to, opt) => {
          to[opt] = props[opt];
          return to;
        }, {})
      : {};

    let FilterForm;
    if (filters) {
      FilterForm = generateForm({
        dataSources: filterDataSources,
        fields: filters,
        formOptions: ['tableKey', ...tableOptions],
        handleSubmit:
          ({ tableKey }) =>
          values => {
            dispatch('APPLY_FILTER_FORM', { tableKey, appliedFilters: values });
            return { values };
          },
      });
    }

    const setProps = {
      // Passed in to `generateTable`
      columns,
      dataSource,
      onValidateFilters,
      // Calculated from props and tableOptions.
      tableOptions: { ...tableOptionProps },
      // Add FilterForm to the components that are passed.
      components: { ...props.components, FilterForm },
      // Sortable can be enabled or disabled for an entire table.
      sortable: typeof sortable !== 'undefined' ? sortable : props.sortable,
      // Explicitly allowed props.
      tableKey: props.tableKey,
      filterFormKey: filterFormKey(props.tableKey),
      addColumns: props.addColumns,
      alterColumns: props.alterColumns,
      alterFilters: mergeDeep(
        props.alterFilters || {},
        Map(props.initialFilterValues)
          .map(initialValue => ({ initialValue }))
          .toObject(),
      ),
      filterSet: props.filterSet,
      filterAutoFocus: props.filterAutoFocus,
      columnSet: props.columnSet,
      columnSetOrder: props.columnSetOrder,
      pageSize: props.pageSize,
      defaultSortColumn: props.defaultSortColumn,
      defaultSortDirection: props.defaultSortDirection,
      omitHeader: props.omitHeader,
      includeFooter: props.includeFooter,
      refreshInterval: props.refreshInterval,
      renderOptions: props.renderOptions,
      uncontrolled: props.uncontrolled,
      // For full client-side tables, with no datasource.
      data: props.data,
      filterForm: !!filters,
      initialFilterValues: props.initialFilterValues || {},
      onSearch: props.onSearch,
      onFetch: props.onFetch,
      onColumnSort: props.onColumnSort,
      onColumnToggle: props.onColumnToggle,
    };

    return <Table {...setProps}>{props.children}</Table>;
  };

export class Table extends Component {
  constructor(props) {
    super(props);
    const { components, tableKey, ...config } = props;
    this.components = components;
    this.config = config;
    this.auto = !tableKey || props.uncontrolled;
    this.tableKey = tableKey || generateKey();
  }

  componentDidMount() {
    if (this.auto) {
      mountTable(this.tableKey);
    }
  }

  componentWillUnmount() {
    if (this.auto) {
      unmountTable(this.tableKey);
    }
  }

  render() {
    const columns = generateColumns(
      this.props.columns,
      this.props.addColumns,
      this.props.alterColumns,
    );
    const allColumns = columns.map(c => c.get('value'));
    const initialColumnSet = List(
      this.props.columnSet
        ? typeof this.props.columnSet === 'function'
          ? this.props.columnSet(allColumns)
          : this.props.columnSet
        : allColumns,
    ).filter(c => columns.find(c2 => c2.get('value') === c));

    const columnComponents = extractColumnComponents(columns);

    return (
      <ComponentConfigContext.Consumer>
        {componentConfig => (
          <TableImpl
            {...this.props}
            components={componentConfig.merge(this.props.components).toJS()}
            columnComponents={columnComponents}
            columns={sortColumns(columns, initialColumnSet)}
            columnSet={initialColumnSet}
            tableKey={this.tableKey}
            auto={this.auto}
          >
            {this.props.children}
          </TableImpl>
        )}
      </ComponentConfigContext.Consumer>
    );
  }
}

Table.propTypes = {
  /** An array or list of data. */
  data: PropTypes.array,
  /** An object describing how a table fetches remote data. */
  dataSource: PropTypes.func,
  /**
   * Calculated row data from the datasource mechanism.
   * @ignore
   */
  rows: PropTypes.instanceOf(List),
  /** An array of the columns that will be rendered on the table. */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      /** The title that will be rendered in the header. */
      title: PropTypes.string,
      /** The value key that will be used to map the column to the data object. */
      value: PropTypes.string,
      /** Flag that determines if the column can be used as a filter. */
      filter: PropTypes.oneOf([
        'includes', // clientSide searching only
        'equals',
        'startsWith',
        'in',
        'between',
        'timeline',
        'lt',
        'lteq',
        'gt',
        'gteq',
      ]),
      /** Initial value for the filter. */
      initial: PropTypes.any,
      /** The type of column this is, typically used for determining the filter component. */
      type: PropTypes.oneOf(['text', 'boolean']),
      /** A function that returns an array of objects with the keys `value` and `label`. */
      options: PropTypes.func,
      /** Flag that determines if the column is sortable.*/
      sortable: PropTypes.bool,
      /** Allows overriding the `HeaderCell`, `BodyCell`, and `FooterCell` for a given column. */
      components: PropTypes.shape({
        HeaderCell: PropTypes.func,
        BodyCell: PropTypes.func,
        FooterCell: PropTypes.func,
        Filter: PropTypes.func,
      }),
    }),
  ).isRequired,
  /** Add additional columns to a table. */
  addColumns: PropTypes.arrayOf(
    PropTypes.shape({
      /** The title that will be rendered in the header. */
      title: PropTypes.string,
      /** The value key that will be used to map the column to the data object. */
      value: PropTypes.string,
      /** Flag that determines if the column can be used as a filter. */
      filterable: PropTypes.bool,
      /** Flag that determines if the column is sortable.*/
      sortable: PropTypes.bool,
      /** Flag that determines if the column is toggleable.*/
      toggleable: PropTypes.bool,
      /** Label used for displaying the column in the column control. Defaults to `title`. */
      columnLabel: PropTypes.string,
      /** Defines whether a certain column should always be first or last. */
      columnOrder: PropTypes.oneOf(['first', 'last']),
      /** Allows overriding the `HeaderCell`, `BodyCell`, and `FooterCell` for a given column. */
      components: PropTypes.shape({
        HeaderCell: PropTypes.func,
        BodyCell: PropTypes.func,
        FooterCell: PropTypes.func,
        Filter: PropTypes.func,
      }),
    }),
  ),
  /** Allow overriding the columns shown and in which order. */
  columnSet: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.func,
  ]),
  components: PropTypes.shape({
    /** Override the default table layout, analogous to `<table>`. */
    TableLayout: PropTypes.func,
    /** Overrides the default filter layout. */
    FilterLayout: PropTypes.func,
    /** Overrides the default text type filter input */
    TextFilter: PropTypes.func,
    /** Overrides the default boolean type filter input */
    BooleanFilter: PropTypes.func,
    /** Override the table header, analogous to `<thead>`. */
    Header: PropTypes.func,
    /** Override the table header row, analogous to the `<tr>` in `<thead>`. */
    HeaderRow: PropTypes.func,
    /** Override the table header cells, analogous to `<th>`. */
    HeaderCell: PropTypes.func,

    /** Override the table body, analogous to `<tbody>`. */
    Body: PropTypes.func,
    /** Override the table rows, analogous to `<tr>`. */
    BodyRow: PropTypes.func,
    /** Override the table body row when there is no data. */
    EmptyBodyRow: PropTypes.func,
    /** Override the table cells, analogous to `<td>`. */
    BodyCell: PropTypes.func,

    /** Override the table footer, analogous to `<tfoot>`. */
    Footer: PropTypes.func,
    /** Override the table footer row, analogous to the `<tr>` in `<tfoot>`. */
    FooterRow: PropTypes.func,
    /** Override the table footer cells, analogous to `<td>`. */
    FooterCell: PropTypes.func,
  }),

  /** Set the page size when paginating data. */
  pageSize: PropTypes.number,

  /**
   * Flag to enable/disable sorting support.
   * @ignore
   */
  sortable: PropTypes.bool,

  /** The column to sort results by initially. */
  defaultSortColumn: PropTypes.string,
  /** The initial sort direction of results. */
  defaultSortDirection: PropTypes.string,

  /** The child of this component should be a function which renders the table layout. */
  children: PropTypes.func.isRequired,

  /** A flag used to suppress the rendering of the table header. */
  omitHeader: PropTypes.bool,
  /** A flag used to enable to rendering of the table footer. */
  includeFooter: PropTypes.bool,
};

const defaultProps = {
  components: {},
  rows: List([]),
  columns: [],
  addColumns: [],
  alterColumns: {},

  sortable: true,
  pageSize: 25,
  omitHeader: false,
  includeFooter: false,
};

Table.defaultProps = defaultProps;

Table.configure = configureTable;
